<template>
  <v-container class="bware-container">
    <!-- Temporärer Material-Import (Admin) -->
    <v-card
      v-if="canImport"
      class="status-overview-card mb-6"
    >
      <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-2">
        <span>DACH Material-Liste importieren</span>
        <v-chip size="small" color="info" variant="tonal">
          {{ materialStats.gesamt.toLocaleString('de-DE') }} Einträge
        </v-chip>
      </v-card-title>
      <v-card-text>
        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
          Excel-Spalten <strong>A–P</strong> markieren und hier einfügen (Tab-getrennt).
          Reihenfolge: CMMF Code, CMMF, Range Code, Range, Model Code, Model,
          Commercial ref, Ean Code, Consumer Brand Code, Consumer Brand,
          Family Level 1 Code/Name, Family Level 2 Code/Name, Product Line Code/Name.
          Bei ~90.000 Zeilen erfolgt der Upload automatisch in Chunks.
          <br>
          <strong>#NV / #N/A</strong> in Spalte B (CMMF) und anderen Spalten überschreiben bestehende Werte
          <strong>nicht</strong>. Bereits gescannte Positionen werden nach dem Import mitaktualisiert.
          Für reine CMMF-Updates „Tabelle komplett leeren“ bitte auslassen.
        </v-alert>

        <v-checkbox
          v-model="importHasHeader"
          label="Erste Zeile ist Kopfzeile (überspringen)"
          density="compact"
          hide-details
          class="mb-2"
          @update:model-value="parseImportPaste"
        />
        <v-checkbox
          v-model="importReplaceAll"
          label="Vor dem Import Tabelle komplett leeren (voller Neuimport – für CMMF-Update auslassen)"
          density="compact"
          hide-details
          class="mb-3"
          color="warning"
        />

        <v-textarea
          v-model="importPasteText"
          label="Excel-Daten hier einfügen (Strg+V)"
          rows="8"
          variant="outlined"
          hide-details
          class="mb-3"
          placeholder="CMMF Code&lt;TAB&gt;CMMF&lt;TAB&gt;…&lt;TAB&gt;Product Line"
          @input="parseImportPaste"
          @paste="onImportPaste"
        />

        <div class="d-flex align-center flex-wrap ga-2 mb-2">
          <v-btn
            color="success"
            :loading="loading.import"
            :disabled="parsedImportRows.length === 0"
            @click="startImport"
          >
            <v-icon start>mdi-database-import</v-icon>
            {{ parsedImportRows.length.toLocaleString('de-DE') }} Zeile(n) importieren
          </v-btn>
          <v-btn variant="text" :disabled="!importPasteText" @click="clearImportPaste">
            Leeren
          </v-btn>
          <v-btn variant="tonal" :loading="loading.materialStats" @click="fetchMaterialStats">
            Stats aktualisieren
          </v-btn>
          <span v-if="importProgress.show" class="text-caption">
            Chunk {{ importProgress.current }} / {{ importProgress.total }}
            · neu {{ importProgress.eingefuegt }}
            · update {{ importProgress.aktualisiert }}
          </span>
        </div>

        <v-alert v-if="importResult.show" :type="importResult.type" class="mt-2">
          <strong>{{ importResult.title }}</strong><br>
          {{ importResult.message }}
        </v-alert>
      </v-card-text>
    </v-card>

    <v-card class="status-overview-card mb-6">
      <v-tabs v-model="activeTab" color="teal" class="px-2 pt-1">
        <v-tab value="bware">
          <v-icon start>mdi-package-variant</v-icon>
          B-Ware Erfassung
        </v-tab>
        <v-tab value="eingangskontrolle">
          <v-icon start>mdi-clipboard-check-outline</v-icon>
          Eingangskontrolle
        </v-tab>
      </v-tabs>
      <v-divider />

      <v-window v-model="activeTab">
        <!-- ========== TAB: B-Ware Erfassung ========== -->
        <v-window-item value="bware">
      <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-2">
        <span>B-Ware Erfassung</span>
        <div class="d-flex ga-2 flex-wrap">
          <v-btn
            color="info"
            variant="outlined"
            :disabled="bulkBwareLabelCount === 0"
            @click="printBulkBwareEtiketten"
          >
            <v-icon start>mdi-tag-multiple</v-icon>
            Etiketten Bulk-Druck
          </v-btn>
          <v-btn
            color="orange-darken-1"
            variant="outlined"
            :disabled="positionen.length === 0"
            :loading="loading.zurueckstellen"
            @click="zurueckstellen"
          >
            <v-icon start>mdi-pause-circle-outline</v-icon>
            Zurückstellen
          </v-btn>
          <v-btn
            color="warning"
            variant="outlined"
            :disabled="positionen.length === 0"
            :loading="loading.clearing"
            @click="dialogs.clearConfirm = true"
          >
            <v-icon start>mdi-archive-check</v-icon>
            Liste abschliessen
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <div class="info-box mb-4">
          <div class="d-flex justify-space-between flex-wrap ga-4">
            <div>
              <div class="info-label">Benutzer</div>
              <div class="info-value text-body-1">{{ userName || '—' }}</div>
            </div>
            <div>
              <div class="info-label">Artikel</div>
              <div class="info-value">{{ statistik.artikel }}</div>
            </div>
            <div>
              <div class="info-label">B-Ware</div>
              <div class="info-value">{{ statistik.bware }}</div>
            </div>
            <div>
              <div class="info-label">Schrott</div>
              <div class="info-value">{{ statistik.schrott }}</div>
            </div>
            <div>
              <div class="info-label">Gesamt</div>
              <div class="info-value">{{ statistik.gesamt }}</div>
            </div>
          </div>
        </div>

        <v-row class="mb-4">
          <v-col cols="12" md="8">
            <v-text-field
              ref="scannerInput"
              v-model="scanInput"
              label="EAN / CMMF scannen"
              placeholder="Barcode hier einscannen..."
              autofocus
              density="comfortable"
              persistent-placeholder
              hide-details
              class="form-field"
              @keyup.enter="handleScan"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model.number="scanMenge"
              label="Menge"
              type="number"
              min="1"
              density="comfortable"
              hide-details
              class="form-field"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-btn
              color="success"
              block
              height="48"
              :loading="loading.scanning"
              @click="handleScan"
            >
              Scannen
            </v-btn>
          </v-col>
        </v-row>

        <v-alert
          v-if="scanFeedback.show"
          :type="scanFeedback.type"
          closable
          class="mb-4"
          @click:close="scanFeedback.show = false"
        >
          {{ scanFeedback.message }}
        </v-alert>

        <v-data-table
          :headers="tableHeaders"
          :items="positionen"
          :loading="loading.positionen"
          density="compact"
          class="mb-2"
        >
          <template #item.bware_menge="{ item }">
            <span class="text-success font-weight-medium">{{ item.bware_menge }}</span>
          </template>
          <template #item.schrott_menge="{ item }">
            <span class="text-error font-weight-medium">{{ item.schrott_menge }}</span>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex ga-1 justify-end">
              <v-btn
                icon
                size="small"
                color="primary"
                title="Etikett drucken"
                :disabled="!item.ean"
                @click="openEtikettDialog(item)"
              >
                <v-icon>mdi-tag-outline</v-icon>
              </v-btn>
              <v-btn icon size="small" color="error" title="Löschen" @click="removePosition(item.id)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
        </v-window-item>

        <!-- ========== TAB: Eingangskontrolle ========== -->
        <v-window-item value="eingangskontrolle">
          <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-2">
            <span>Eingangskontrolle</span>
            <div class="d-flex ga-2 flex-wrap">
              <v-btn
                color="primary"
                variant="outlined"
                :disabled="ekPositionen.length === 0"
                @click="printEkScheine"
              >
                <v-icon start>mdi-printer</v-icon>
                Scheine drucken
              </v-btn>
              <v-btn
                color="error"
                variant="outlined"
                :disabled="ekPositionen.length === 0"
                @click="clearEkListe"
              >
                <v-icon start>mdi-delete-sweep</v-icon>
                Liste leeren
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <v-row class="mb-4" align="center">
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="ekScanMenge"
                  type="number"
                  min="1"
                  label="Menge"
                  density="comfortable"
                  hide-details
                  @keyup.enter="focusEkScanner"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  ref="ekScannerField"
                  v-model="ekScanInput"
                  label="EAN / CMMF scannen"
                  density="comfortable"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-barcode-scan"
                  @keyup.enter="handleEkScan"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  color="primary"
                  block
                  size="large"
                  :loading="loading.scanning"
                  :disabled="!ekScanInput"
                  @click="handleEkScan"
                >
                  <v-icon start>mdi-plus</v-icon>
                  Hinzufügen
                </v-btn>
              </v-col>
            </v-row>

            <v-alert
              v-if="ekScanFeedback.show"
              :type="ekScanFeedback.type"
              closable
              class="mb-4"
              @click:close="ekScanFeedback.show = false"
            >
              {{ ekScanFeedback.message }}
            </v-alert>

            <v-data-table
              :headers="tableHeaders"
              :items="ekPositionen"
              density="compact"
              class="mb-2"
            >
              <template #item.bware_menge="{ item }">
                <span class="text-success font-weight-medium">{{ item.bware_menge }}</span>
              </template>
              <template #item.schrott_menge="{ item }">
                <span class="text-error font-weight-medium">{{ item.schrott_menge }}</span>
              </template>
              <template #item.actions="{ item }">
                <div class="d-flex ga-1 justify-end">
                  <v-btn
                    icon
                    size="small"
                    color="error"
                    title="Löschen"
                    @click="removeEkPosition(item.id)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <div class="text-caption text-medium-emphasis mt-2">
              Nur Scannen und Scheine drucken – es wird nichts gespeichert.
              Palettenschein: Anzahl ohne Schrott. Eingangskontrolle-Schein: Gesamtzahl inkl. Schrott.
            </div>
          </v-card-text>
        </v-window-item>
      </v-window>
    </v-card>

    <!-- Etikettendruck (Brother: 90 mm Laenge x 26 mm Bandbreite) -->
    <v-dialog v-model="dialogs.etikett" max-width="760" persistent>
      <v-card class="dialog-card">
        <v-card-title>Etikett drucken</v-card-title>
        <v-card-text>
          <div class="etikett-dialog-layout">
            <div class="etikett-preview-col">
              <div class="etikett-preview-label">Vorschau 90x26 mm</div>
              <div class="etikett-preview-stage">
                <div class="etikett-backing">
                  <div class="etikett-preview">
                    <div class="etikett-preview-inner">
                      <div class="etikett-preview-barcode-wrap">
                        <img
                          v-if="etikettPreviewBarcode"
                          class="etikett-preview-barcode"
                          :src="etikettPreviewBarcode"
                          alt="EAN"
                        />
                      </div>
                      <div class="etikett-preview-texts">
                        <div class="etikett-preview-typ">{{ etikettTypLabel }}</div>
                        <div class="etikett-preview-line">{{ etikettData.item?.bezeichnung || '—' }}</div>
                        <div class="etikett-preview-line">{{ etikettData.item?.cmmf_nummer || '—' }}</div>
                        <div class="etikett-preview-line">{{ etikettData.item?.commercial_ref || '—' }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="etikett-controls-col">
              <v-radio-group
                v-if="etikettTypOptions.length > 1"
                v-model="etikettData.typ"
                label="Kennzeichnung"
                density="compact"
                hide-details
                class="mb-3"
                @update:model-value="onEtikettTypChange"
              >
                <v-radio
                  v-for="opt in etikettTypOptions"
                  :key="opt.value"
                  :label="opt.title"
                  :value="opt.value"
                />
              </v-radio-group>
              <v-alert
                v-else-if="etikettTypOptions.length === 1"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                Kennzeichnung: <strong>{{ etikettTypOptions[0].title }}</strong>
              </v-alert>
              <v-text-field
                v-model.number="etikettData.anzahl"
                type="number"
                min="1"
                max="99"
                label="Anzahl Etiketten"
                density="comfortable"
                hide-details
                class="mb-2"
              />
              <div class="text-caption text-medium-emphasis">
                Format 90x26 mm (quer), 3 mm Rand – Brother P-touch waehlen.
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogs.etikett = false">Abbrechen</v-btn>
          <v-btn
            color="primary"
            :loading="loading.etikett"
            :disabled="!etikettData.typ || etikettData.anzahl < 1 || !etikettData.item?.ean"
            @click="printEtiketten"
          >
            <v-icon start>mdi-printer</v-icon>
            Drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Klassifizierung -->
    <v-dialog v-model="dialogs.classify" max-width="520" persistent>
      <v-card class="dialog-card">
        <v-card-title>Ware klassifizieren</v-card-title>
        <v-card-text>
          <div v-if="pendingArticle" class="mb-4">
            <div><strong>Consumer Brand:</strong> {{ pendingArticle.brand || '—' }}</div>
            <div><strong>Bezeichnung:</strong> {{ pendingArticle.kurztext || '—' }}</div>
            <div><strong>Commercial Ref:</strong> {{ pendingArticle.commercial_ref || '—' }}</div>
            <div><strong>CMMF:</strong> {{ pendingArticle.cmmf_nummer }}</div>
            <div><strong>EAN:</strong> {{ pendingArticle.gtin_ean || pendingEan || '—' }}</div>
            <div class="mt-2"><strong>Menge:</strong> {{ pendingMenge }}</div>
          </div>
          <p class="mb-0">Ist die Ware <strong>B-Ware</strong> oder <strong>Schrott</strong>?</p>
        </v-card-text>
        <v-card-actions class="pa-4 flex-wrap ga-2">
          <v-spacer />
          <v-btn variant="text" @click="cancelClassify">Abbrechen</v-btn>
          <v-btn
            color="error"
            variant="tonal"
            :loading="loading.scanning"
            @click="confirmClassify('schrott')"
          >
            Schrott
          </v-btn>
          <v-btn color="success" :loading="loading.scanning" @click="confirmClassify('bware')">
            B-Ware
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mehrfach-Treffer -->
    <v-dialog v-model="dialogs.scanMultiHit" max-width="960" persistent scrollable>
      <v-card class="dialog-card">
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon color="warning">mdi-alert</v-icon>
          Mehrere Artikel für „{{ scanMultiHit.ean }}"
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-3">
            {{ scanMultiHit.artikel.length }} Treffer in der DACH Material-Liste.
            Bitte auswählen – Menge: <strong>{{ scanMultiHit.menge }}</strong> Stk.
          </v-alert>
          <v-table density="compact">
            <thead>
              <tr>
                <th>Consumer Brand</th>
                <th>Bezeichnung</th>
                <th>Commercial Ref</th>
                <th>CMMF</th>
                <th>EAN</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in scanMultiHit.artikel" :key="a.id">
                <td>{{ a.brand || '–' }}</td>
                <td>{{ a.kurztext || '–' }}</td>
                <td>{{ a.commercial_ref || '–' }}</td>
                <td><strong>{{ a.cmmf_nummer }}</strong></td>
                <td>{{ a.gtin_ean || '–' }}</td>
                <td class="text-end">
                  <v-btn
                    size="small"
                    color="success"
                    :loading="loading.scanning"
                    @click="selectMultiHit(a)"
                  >
                    Auswählen
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="loading.scanning" @click="cancelMultiHit">Abbrechen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.eanNotFound" max-width="420">
      <v-card class="dialog-card">
        <v-card-title>Artikel nicht gefunden</v-card-title>
        <v-card-text>
          Der Code <code>{{ notFoundEan }}</code> ist in der DACH Material-Liste nicht vorhanden.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="closeNotFound">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.clearConfirm" max-width="480" persistent>
      <v-card class="dialog-card">
        <v-card-title>Liste abschliessen &amp; Scheine drucken?</v-card-title>
        <v-card-text>
          Die {{ positionen.length }} Positionen werden archiviert und erhalten eine B-Ware-ID.
          Anschließend werden Schein und Palettenschein mit dieser ID gedruckt.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogs.clearConfirm = false">Abbrechen</v-btn>
          <v-btn color="warning" :loading="loading.clearing" @click="clearPositionen">
            Abschliessen &amp; drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Archiv abgeschlossener Listen (nur B-Ware) -->
    <v-card v-if="activeTab === 'bware'" class="status-overview-card mb-6 mt-6">
      <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-2">
        <span>Abgeschlossene Listen (Archiv)</span>
        <v-chip size="small" color="info" variant="tonal">
          {{ archivListen.length }} Listen
        </v-chip>
      </v-card-title>
      <v-card-text>
        <v-alert
          v-if="archivListen.length === 0"
          type="info"
          variant="tonal"
          density="compact"
        >
          Noch keine abgeschlossenen Listen vorhanden.
        </v-alert>
        <v-table v-else density="compact">
          <thead>
            <tr>
              <th>B-Ware-ID</th>
              <th>Status</th>
              <th>Ersteller</th>
              <th>Bearbeiter</th>
              <th>Erstellt am</th>
              <th>Abgeschlossen am</th>
              <th class="text-center">Artikel</th>
              <th class="text-center">B-Ware</th>
              <th class="text-center">Schrott</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="liste in archivListen" :key="liste.id">
              <td class="font-weight-bold">{{ liste.bware_id || '—' }}</td>
              <td>
                <v-chip
                  :color="liste.status === 'abgeschlossen' ? 'success' : 'orange'"
                  size="small"
                  variant="tonal"
                >
                  {{ liste.status === 'abgeschlossen' ? 'Abgeschlossen' : 'Zurückgestellt' }}
                </v-chip>
              </td>
              <td>{{ liste.erstellt_von_name || '—' }}</td>
              <td>{{ liste.user_name || '—' }}</td>
              <td>{{ new Date(liste.erstellt_am).toLocaleString('de-DE') }}</td>
              <td>{{
                liste.abgeschlossen_am
                  ? new Date(liste.abgeschlossen_am).toLocaleString('de-DE')
                  : '—'
              }}</td>
              <td class="text-center">{{ liste.anzahl_positionen }}</td>
              <td class="text-center text-success">{{ liste.summe_bware }}</td>
              <td class="text-center text-error">{{ liste.summe_schrott }}</td>
              <td class="text-end">
                <div class="d-flex ga-1 justify-end">
                  <v-btn
                    v-if="liste.status === 'zurueckgestellt'"
                    icon
                    size="small"
                    color="warning"
                    title="Liste übernehmen und weiterbearbeiten"
                    @click="uebernehmenListe(liste.id)"
                  >
                    <v-icon>mdi-hand-extended</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    size="small"
                    color="primary"
                    title="Liste ansehen & drucken"
                    @click="openArchivListe(liste.id)"
                  >
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>

    <!-- Archiv-Detail-Dialog -->
    <v-dialog v-model="archivDetail.show" max-width="960" scrollable>
      <v-card class="dialog-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-2">
          <span>
            {{ archivDetail.liste?.bware_id || `Liste #${archivDetail.liste?.id}` }}
            – {{ archivDetail.liste?.user_name || '—' }}
          </span>
          <v-chip
            size="small"
            :color="archivDetail.liste?.status === 'abgeschlossen' ? 'success' : 'orange'"
            variant="tonal"
          >
            {{ archivDetail.liste?.status === 'abgeschlossen' ? 'Abgeschlossen' : 'Zurückgestellt' }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <div class="d-flex ga-4 mb-4 flex-wrap">
            <div>
              <div class="text-caption text-medium-emphasis">Erstellt</div>
              <div>{{
                archivDetail.liste?.erstellt_am
                  ? new Date(archivDetail.liste.erstellt_am).toLocaleString('de-DE')
                  : '—'
              }}</div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">Abgeschlossen</div>
              <div>{{
                archivDetail.liste?.abgeschlossen_am
                  ? new Date(archivDetail.liste.abgeschlossen_am).toLocaleString('de-DE')
                  : '—'
              }}</div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">Artikel</div>
              <div>{{ archivDetail.statistik?.artikel ?? 0 }}</div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">B-Ware</div>
              <div class="text-success">{{ archivDetail.statistik?.bware ?? 0 }}</div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">Schrott</div>
              <div class="text-error">{{ archivDetail.statistik?.schrott ?? 0 }}</div>
            </div>
          </div>

          <v-data-table
            :headers="tableHeaders"
            :items="archivDetail.positionen"
            density="compact"
            class="mb-2"
          >
            <template #item.bware_menge="{ item }">
              <span class="text-success font-weight-medium">{{ item.bware_menge }}</span>
            </template>
            <template #item.schrott_menge="{ item }">
              <span class="text-error font-weight-medium">{{ item.schrott_menge }}</span>
            </template>
            <template #item.actions="{ item }">
              <v-btn
                icon
                size="small"
                color="primary"
                title="Etikett drucken"
                :disabled="!item.ean"
                @click="openEtikettDialog(item)"
              >
                <v-icon>mdi-tag-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="outlined"
            :disabled="archivDetail.positionen.length === 0"
            @click="printArchivSchein"
          >
            <v-icon start>mdi-printer</v-icon>
            Schein drucken
          </v-btn>
          <v-btn
            color="secondary"
            variant="outlined"
            :disabled="archivDetail.positionen.length === 0"
            @click="printArchivPalettenSchein"
          >
            <v-icon start>mdi-printer</v-icon>
            Palettenschein
          </v-btn>
          <v-btn
            color="info"
            variant="outlined"
            :disabled="archivBwareLabelCount === 0"
            @click="printArchivBulkEtiketten"
          >
            <v-icon start>mdi-tag-multiple</v-icon>
            Etiketten Bulk
          </v-btn>
          <v-btn variant="text" @click="archivDetail.show = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
/**
 * B-Ware Erfassung – Stammdaten aus dach_material_list.
 */
import { ref, computed, nextTick, onMounted, watch } from 'vue';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { API_BASE_URL } from '@/config';
import { useAuthStore } from '@/stores/auth';

const authStore = useAuthStore();
const toast = useToast();
const apiUrl = () => 'bware/index.php';
const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const canImport = computed(
  () =>
    !!authStore.user?.admin ||
    !!authStore.user?.super_admin ||
    !!authStore.user?.developer
);

/** @type {import('vue').Ref<'bware'|'eingangskontrolle'>} */
const activeTab = ref('bware');
/** Welcher Scan-Flow Classify/MultiHit geoeffnet hat. */
const scanTarget = ref('bware');

const scannerInput = ref(null);
const scanInput = ref('');
const scanMenge = ref(1);
const positionen = ref([]);
const userName = ref('');
const activeListeId = ref(null);
const archivListen = ref([]);
const archivDetail = ref({ show: false, liste: null, positionen: [], statistik: null });
const statistik = ref({ artikel: 0, bware: 0, schrott: 0, gesamt: 0 });
const notFoundEan = ref('');
const pendingArticle = ref(null);
const pendingEan = ref('');
const pendingMenge = ref(1);
const scanMultiHit = ref({ ean: '', menge: 1, artikel: [] });
const scanFeedback = ref({ show: false, type: 'success', message: '' });

const ekScannerField = ref(null);
const ekScanInput = ref('');
const ekScanMenge = ref(1);
const ekPositionen = ref([]);
let ekNextId = 1;
const ekScanFeedback = ref({ show: false, type: 'success', message: '' });

const materialStats = ref({ gesamt: 0, mit_ean: 0, letzte_aktualisierung: null });
const importPasteText = ref('');
const importHasHeader = ref(true);
const importReplaceAll = ref(false);
const parsedImportRows = ref([]);
const importProgress = ref({
  show: false,
  current: 0,
  total: 0,
  eingefuegt: 0,
  aktualisiert: 0,
});
const importResult = ref({ show: false, type: 'success', title: '', message: '' });

const loading = ref({
  positionen: false,
  scanning: false,
  printing: false,
  clearing: false,
  zurueckstellen: false,
  import: false,
  materialStats: false,
  etikett: false,
});

const dialogs = ref({
  classify: false,
  scanMultiHit: false,
  eanNotFound: false,
  clearConfirm: false,
  etikett: false,
});

const etikettData = ref({
  item: null,
  typ: 'bware',
  anzahl: 1,
});

const etikettPreviewBarcode = ref('');

const etikettTypLabel = computed(() =>
  etikettData.value.typ === 'schrott' ? 'Schrott' : 'B-Ware'
);

const etikettPreviewEan = computed(() =>
  normalizeEan13(String(etikettData.value.item?.ean || ''))
);

const bulkBwareLabelCount = computed(() =>
  positionen.value.reduce((sum, item) => sum + Math.max(0, Number(item?.bware_menge) || 0), 0)
);

const etikettTypOptions = computed(() => {
  const item = etikettData.value.item;
  if (!item) return [];
  const opts = [];
  if (Number(item.bware_menge) > 0) {
    opts.push({ title: `B-Ware (${item.bware_menge} Stk.)`, value: 'bware' });
  }
  if (Number(item.schrott_menge) > 0) {
    opts.push({ title: `Schrott (${item.schrott_menge} Stk.)`, value: 'schrott' });
  }
  if (opts.length === 0) {
    opts.push({ title: 'B-Ware', value: 'bware' });
  }
  return opts;
});

const tableHeaders = [
  { title: 'Consumer Brand', key: 'consumer_brand', sortable: true },
  { title: 'Bezeichnung', key: 'bezeichnung', sortable: true },
  { title: 'Commercial Ref', key: 'commercial_ref', sortable: true },
  { title: 'CMMF', key: 'cmmf_nummer', sortable: true },
  { title: 'EAN', key: 'ean', sortable: true },
  { title: 'B-Ware', key: 'bware_menge', sortable: true, align: 'center' },
  { title: 'Schrott', key: 'schrott_menge', sortable: true, align: 'center' },
  { title: '', key: 'actions', sortable: false, align: 'end' },
];

const IMPORT_CHUNK_SIZE = 2000;

const showScanFeedback = (type, message) => {
  scanFeedback.value = { show: true, type, message };
};

const showEkScanFeedback = (type, message) => {
  ekScanFeedback.value = { show: true, type, message };
};

const applyListResponse = (data) => {
  if (Array.isArray(data.positionen)) {
    positionen.value = data.positionen;
  }
  if (data.statistik) {
    statistik.value = data.statistik;
  }
  if (typeof data.user_name === 'string') {
    userName.value = data.user_name;
  }
  if (data.liste_id != null) {
    activeListeId.value = data.liste_id;
  }
};

const focusScanner = () => {
  nextTick(() => {
    scannerInput.value?.focus?.();
  });
};

const focusEkScanner = () => {
  nextTick(() => {
    ekScannerField.value?.focus?.();
  });
};

const focusActiveScanner = () => {
  if (scanTarget.value === 'eingangskontrolle' || activeTab.value === 'eingangskontrolle') {
    focusEkScanner();
  } else {
    focusScanner();
  }
};

watch(activeTab, (tab) => {
  nextTick(() => {
    if (tab === 'eingangskontrolle') {
      focusEkScanner();
    } else {
      focusScanner();
    }
  });
});

const fetchPositionen = async () => {
  loading.value.positionen = true;
  try {
    const response = await client.get(apiUrl(), { params: { action: 'get_positionen' } });
    if (response.data?.status === 'success') {
      applyListResponse(response.data);
    } else {
      toast.error(response.data?.message || 'Liste konnte nicht geladen werden');
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Laden der Scan-Liste');
  } finally {
    loading.value.positionen = false;
  }
};

const fetchMaterialStats = async () => {
  loading.value.materialStats = true;
  try {
    const response = await client.get(apiUrl(), { params: { action: 'get_material_stats' } });
    if (response.data?.status === 'success') {
      materialStats.value = {
        gesamt: Number(response.data.gesamt || 0),
        mit_ean: Number(response.data.mit_ean || 0),
        letzte_aktualisierung: response.data.letzte_aktualisierung || null,
      };
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value.materialStats = false;
  }
};

const onImportPaste = () => {
  setTimeout(() => parseImportPaste(), 0);
};

const clearImportPaste = () => {
  importPasteText.value = '';
  parsedImportRows.value = [];
  importResult.value.show = false;
  importProgress.value.show = false;
};

/**
 * Parst Excel-Paste (A–P, Tab oder Semikolon).
 */
const parseImportPaste = () => {
  const text = String(importPasteText.value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  if (!text.trim()) {
    parsedImportRows.value = [];
    return;
  }

  const lines = text.split('\n').filter((l) => l.trim() !== '');
  const startIdx = importHasHeader.value && lines.length > 1 ? 1 : 0;
  const out = [];

  for (let i = startIdx; i < lines.length; i++) {
    const line = lines[i];
    const sep = line.includes('\t') ? '\t' : line.includes(';') ? ';' : '\t';
    const parts = line.split(sep).map((s) => s.trim());
    const cmmfCode = String(parts[0] || '').replace(/\s+/g, '');
    if (!cmmfCode) {
      continue;
    }
    out.push({
      cmmf_code: cmmfCode,
      cmmf: parts[1] || '',
      range_code: parts[2] || '',
      range_name: parts[3] || '',
      model_code: parts[4] || '',
      model_name: parts[5] || '',
      commercial_ref: parts[6] || '',
      ean_code: String(parts[7] || '').replace(/\D/g, ''),
      consumer_brand_code: parts[8] || '',
      consumer_brand: parts[9] || '',
      family_level1_code: parts[10] || '',
      family_level1: parts[11] || '',
      family_level2_code: parts[12] || '',
      family_level2: parts[13] || '',
      product_line_code: parts[14] || '',
      product_line: parts[15] || '',
    });
  }
  parsedImportRows.value = out;
};

const startImport = async () => {
  if (parsedImportRows.value.length === 0) {
    toast.error('Keine Daten zum Importieren');
    return;
  }

  loading.value.import = true;
  importResult.value.show = false;

  const rows = parsedImportRows.value;
  const chunks = [];
  for (let i = 0; i < rows.length; i += IMPORT_CHUNK_SIZE) {
    chunks.push(rows.slice(i, i + IMPORT_CHUNK_SIZE));
  }

  importProgress.value = {
    show: true,
    current: 0,
    total: chunks.length,
    eingefuegt: 0,
    aktualisiert: 0,
  };

  let uebersprungen = 0;
  let nvUebersprungen = 0;
  let scansAktualisiert = 0;
  let geloescht = 0;
  const fehler = [];

  try {
    for (let i = 0; i < chunks.length; i++) {
      importProgress.value.current = i + 1;
      const response = await client.post(apiUrl(), {
        action: 'import_material_rows',
        rows: chunks[i],
        replace_all: importReplaceAll.value,
        chunk_index: i,
        is_last_chunk: i === chunks.length - 1,
      });

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || `Chunk ${i + 1} fehlgeschlagen`);
      }

      importProgress.value.eingefuegt += Number(response.data.eingefuegt || 0);
      importProgress.value.aktualisiert += Number(response.data.aktualisiert || 0);
      uebersprungen += Number(response.data.uebersprungen || 0);
      nvUebersprungen += Number(response.data.nv_uebersprungen || 0);
      scansAktualisiert += Number(response.data.scans_aktualisiert || 0);
      if (i === 0) {
        geloescht = Number(response.data.geloescht || 0);
      }
      if (Array.isArray(response.data.fehler)) {
        fehler.push(...response.data.fehler);
      }
    }

    importResult.value = {
      show: true,
      type: 'success',
      title: 'Import abgeschlossen',
      message:
        `${rows.length.toLocaleString('de-DE')} Zeilen verarbeitet · ` +
        `neu ${importProgress.value.eingefuegt.toLocaleString('de-DE')} · ` +
        `update ${importProgress.value.aktualisiert.toLocaleString('de-DE')} · ` +
        `übersprungen ${uebersprungen}` +
        (nvUebersprungen ? ` · #NV belassen ${nvUebersprungen.toLocaleString('de-DE')}` : '') +
        (scansAktualisiert
          ? ` · Scan-Positionen aktualisiert ${scansAktualisiert.toLocaleString('de-DE')}`
          : '') +
        (geloescht ? ` · vorher gelöscht ${geloescht.toLocaleString('de-DE')}` : '') +
        (fehler.length ? ` · Hinweise: ${fehler.slice(0, 3).join('; ')}` : ''),
    };
    toast.success('Material-Liste importiert');
    await fetchMaterialStats();
  } catch (error) {
    console.error(error);
    const apiMsg = error?.response?.data?.message || error?.message;
    importResult.value = {
      show: true,
      type: 'error',
      title: 'Import fehlgeschlagen',
      message: apiMsg || 'Unbekannter Fehler',
    };
    toast.error(apiMsg || 'Import fehlgeschlagen');
  } finally {
    loading.value.import = false;
  }
};

/**
 * Gemeinsame EAN-Suche fuer B-Ware und Eingangskontrolle.
 * @param {string} code
 * @param {number} menge
 * @param {'bware'|'eingangskontrolle'} target
 * @param {(type: string, message: string) => void} feedbackFn
 * @param {() => void} clearInputFn
 */
const runEanSearch = async (code, menge, target, feedbackFn, clearInputFn) => {
  scanTarget.value = target;
  loading.value.scanning = true;
  try {
    const response = await client.get(apiUrl(), {
      params: { action: 'search_ean', ean: code },
    });

    if (response.data?.status === 'success' && response.data.artikel) {
      const raw = response.data.artikel;
      const treffer = Array.isArray(raw) ? raw : Object.values(raw);
      if (treffer.length > 1) {
        scanMultiHit.value = {
          ean: code,
          menge: menge || 1,
          artikel: treffer,
        };
        dialogs.value.scanMultiHit = true;
        clearInputFn();
        return;
      }
      if (treffer.length === 1) {
        openClassify(treffer[0], code, menge || 1);
        return;
      }
    }
    notFoundEan.value = code;
    clearInputFn();
    dialogs.value.eanNotFound = true;
  } catch (error) {
    console.error(error);
    const apiMsg = error?.response?.data?.message;
    feedbackFn('error', apiMsg ? `Fehler beim Scannen: ${apiMsg}` : 'Fehler beim Scannen');
  } finally {
    loading.value.scanning = false;
  }
};

const handleScan = async () => {
  const code = scanInput.value.trim();
  if (!code) {
    showScanFeedback('warning', 'Bitte einen Code eingeben');
    return;
  }
  await runEanSearch(code, scanMenge.value || 1, 'bware', showScanFeedback, () => {
    scanInput.value = '';
    scanMenge.value = 1;
  });
};

const handleEkScan = async () => {
  const code = ekScanInput.value.trim();
  if (!code) {
    showEkScanFeedback('warning', 'Bitte einen Code eingeben');
    return;
  }
  await runEanSearch(code, ekScanMenge.value || 1, 'eingangskontrolle', showEkScanFeedback, () => {
    ekScanInput.value = '';
    ekScanMenge.value = 1;
  });
};

const openClassify = (artikel, ean, menge) => {
  pendingArticle.value = artikel;
  pendingEan.value = ean;
  pendingMenge.value = menge;
  dialogs.value.classify = true;
  if (scanTarget.value === 'eingangskontrolle') {
    ekScanInput.value = '';
    ekScanMenge.value = 1;
  } else {
    scanInput.value = '';
    scanMenge.value = 1;
  }
};

const selectMultiHit = (artikel) => {
  dialogs.value.scanMultiHit = false;
  openClassify(artikel, scanMultiHit.value.ean, scanMultiHit.value.menge || 1);
  scanMultiHit.value = { ean: '', menge: 1, artikel: [] };
};

const cancelMultiHit = () => {
  dialogs.value.scanMultiHit = false;
  scanMultiHit.value = { ean: '', menge: 1, artikel: [] };
  focusActiveScanner();
};

const cancelClassify = () => {
  dialogs.value.classify = false;
  pendingArticle.value = null;
  pendingEan.value = '';
  pendingMenge.value = 1;
  focusActiveScanner();
};

/**
 * Lokale Position in der Eingangskontrolle erhoehen/anlegen (ohne DB).
 * @param {'bware'|'schrott'} typ
 */
const confirmEkClassify = (typ) => {
  const art = pendingArticle.value;
  if (!art) return;
  const menge = Number(pendingMenge.value) || 1;
  const materialId = Number(art.id);
  const existing = ekPositionen.value.find((p) => Number(p.material_id) === materialId);
  if (existing) {
    if (typ === 'bware') {
      existing.bware_menge = Number(existing.bware_menge || 0) + menge;
    } else {
      existing.schrott_menge = Number(existing.schrott_menge || 0) + menge;
    }
  } else {
    ekPositionen.value.push({
      id: ekNextId++,
      material_id: materialId,
      consumer_brand: art.brand || art.consumer_brand || '',
      bezeichnung: art.kurztext || art.bezeichnung || '',
      commercial_ref: art.commercial_ref || '',
      cmmf_nummer: art.cmmf_nummer || '',
      ean: pendingEan.value || art.gtin_ean || '',
      bware_menge: typ === 'bware' ? menge : 0,
      schrott_menge: typ === 'schrott' ? menge : 0,
    });
  }
  const label = typ === 'bware' ? 'B-Ware' : 'Schrott';
  showEkScanFeedback('success', `${label}: ${art.kurztext || art.cmmf_nummer}`);
  dialogs.value.classify = false;
  pendingArticle.value = null;
  pendingEan.value = '';
  pendingMenge.value = 1;
  focusEkScanner();
};

const confirmClassify = async (typ) => {
  if (!pendingArticle.value) return;
  if (scanTarget.value === 'eingangskontrolle') {
    confirmEkClassify(typ);
    return;
  }
  loading.value.scanning = true;
  try {
    const response = await client.post(apiUrl(), {
      action: 'scan_article',
      material_id: pendingArticle.value.id,
      typ,
      menge: pendingMenge.value,
      ean: pendingEan.value || pendingArticle.value.gtin_ean || '',
    });
    if (response.data?.status === 'success') {
      applyListResponse(response.data);
      const label = typ === 'bware' ? 'B-Ware' : 'Schrott';
      showScanFeedback(
        'success',
        `${label}: ${pendingArticle.value.kurztext || pendingArticle.value.cmmf_nummer}`
      );
      dialogs.value.classify = false;
      pendingArticle.value = null;
      pendingEan.value = '';
      pendingMenge.value = 1;
      focusScanner();
    } else {
      toast.error(response.data?.message || 'Erfassen fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    const apiMsg = error?.response?.data?.message;
    toast.error(apiMsg ? `Fehler beim Erfassen: ${apiMsg}` : 'Fehler beim Erfassen');
  } finally {
    loading.value.scanning = false;
  }
};

const closeNotFound = () => {
  dialogs.value.eanNotFound = false;
  notFoundEan.value = '';
  focusActiveScanner();
};

/**
 * Entfernt eine lokale Eingangskontrolle-Position.
 * @param {number} positionId
 */
const removeEkPosition = (positionId) => {
  ekPositionen.value = ekPositionen.value.filter((p) => p.id !== positionId);
};

/** Leert die lokale Eingangskontrolle-Liste. */
const clearEkListe = () => {
  ekPositionen.value = [];
  toast.success('Liste geleert');
  focusEkScanner();
};

/** Druckt Eingangskontrolle-Schein + Palettenschein (ohne Speichern). */
const printEkScheine = () => {
  if (ekPositionen.value.length === 0) {
    toast.warning('Keine Positionen zum Drucken');
    return;
  }
  openScheinPrintWindow(buildBeideScheineHtml(ekPositionen.value, null, 'eingangskontrolle'));
};

const removePosition = async (positionId) => {
  try {
    const response = await client.post(apiUrl(), {
      action: 'remove_position',
      position_id: positionId,
    });
    if (response.data?.status === 'success') {
      applyListResponse(response.data);
      toast.success('Position gelöscht');
    } else {
      toast.error(response.data?.message || 'Löschen fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Löschen');
  }
};

const clearPositionen = async () => {
  if (positionen.value.length === 0) {
    toast.warning('Keine Positionen zum Abschließen');
    return;
  }

  const posSnapshot = [...positionen.value];
  loading.value.clearing = true;
  try {
    const response = await client.post(apiUrl(), { action: 'clear_positionen' });
    if (response.data?.status === 'success') {
      const bwareId = response.data.bware_id || null;
      applyListResponse(response.data);
      dialogs.value.clearConfirm = false;

      openScheinPrintWindow(buildBeideScheineHtml(posSnapshot, bwareId));

      toast.success(
        bwareId
          ? `Liste abgeschlossen als ${bwareId} – Scheine werden gedruckt`
          : 'Liste abgeschlossen und archiviert'
      );
      fetchArchiv();
      focusScanner();
    } else {
      toast.error(response.data?.message || 'Abschluss fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Abschließen');
  } finally {
    loading.value.clearing = false;
  }
};

/**
 * Stellt die aktuelle Liste zurück (pausiert) und startet eine neue.
 */
const zurueckstellen = async () => {
  loading.value.zurueckstellen = true;
  try {
    const response = await client.post(apiUrl(), { action: 'zurueckstellen' });
    if (response.data?.status === 'success') {
      applyListResponse(response.data);
      toast.success('Liste zurückgestellt – neue Liste gestartet');
      fetchArchiv();
      focusScanner();
    } else {
      toast.error(response.data?.message || 'Zurückstellen fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Zurückstellen');
  } finally {
    loading.value.zurueckstellen = false;
  }
};

/**
 * Übernimmt eine zurückgestellte Liste und macht sie zur aktiven Liste.
 * @param {number} listeId
 */
const uebernehmenListe = async (listeId) => {
  try {
    const response = await client.post(apiUrl(), { action: 'uebernehmen', liste_id: listeId });
    if (response.data?.status === 'success') {
      applyListResponse(response.data);
      toast.success(response.data.message || 'Liste übernommen');
      fetchArchiv();
      focusScanner();
    } else {
      toast.error(response.data?.message || 'Übernahme fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler bei der Übernahme');
  }
};

/**
 * Lädt die archivierten (abgeschlossenen) Listen.
 */
const fetchArchiv = async () => {
  try {
    const response = await client.get(apiUrl(), { params: { action: 'get_archiv' } });
    if (response.data?.status === 'success') {
      archivListen.value = response.data.listen || [];
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * Lädt die Positionen einer archivierten Liste.
 * @param {number} listeId
 */
const openArchivListe = async (listeId) => {
  try {
    const response = await client.get(apiUrl(), {
      params: { action: 'get_liste_positionen', liste_id: listeId },
    });
    if (response.data?.status === 'success') {
      archivDetail.value = {
        show: true,
        liste: response.data.liste,
        positionen: response.data.positionen || [],
        statistik: response.data.statistik,
      };
    } else {
      toast.error(response.data?.message || 'Liste konnte nicht geladen werden');
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Laden der Liste');
  }
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Normalisiert EAN auf 13 Ziffern (Checkdigit ggf. berechnen).
 * @param {string} raw
 * @returns {string}
 */
const normalizeEan13 = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 13) {
    return digits;
  }
  if (digits.length === 12) {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const n = Number(digits[i]);
      sum += i % 2 === 0 ? n : n * 3;
    }
    const check = (10 - (sum % 10)) % 10;
    return digits + String(check);
  }
  return digits;
};

/**
 * Erzeugt EAN-13 als SVG-Data-URL (Brother-Stil: Guard-Bars, Zifferngruppen).
 * @param {string} ean
 * @returns {string}
 */
const createBarcodeDataUrl = (ean, showValue = true) => {
  const value = normalizeEan13(ean);
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const common = {
    background: '#ffffff',
    lineColor: '#000000',
    displayValue: showValue,
    font: 'Arial',
    fontOptions: '',
    textAlign: 'center',
    textMargin: 0,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 2,
    marginRight: 2,
  };

  try {
    if (value.length === 13) {
      // flat:false = lange Guard-Bars + Ziffern in den Luecken (wie auf dem Foto)
      JsBarcode(svg, value, {
        ...common,
        format: 'EAN13',
        width: 1.7,
        height: 72,
        fontSize: 14,
        flat: false,
      });
    } else {
      JsBarcode(svg, value || String(ean || ''), {
        ...common,
        format: 'CODE128',
        width: 1.6,
        height: 64,
        fontSize: 14,
      });
    }
  } catch (e) {
    console.warn('Barcode-Fallback CODE128', e);
    JsBarcode(svg, String(ean || value || ''), {
      ...common,
      format: 'CODE128',
      width: 1.6,
      height: 64,
      fontSize: 14,
    });
  }

  const svgMarkup = new window.XMLSerializer().serializeToString(svg);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
};

/**
 * Anzahl-Vorschlag anhand Typ und Mengen der Position.
 * @param {Record<string, unknown>} item
 * @param {'bware'|'schrott'} typ
 * @returns {number}
 */
const suggestEtikettAnzahl = (item, typ) => {
  if (typ === 'schrott') {
    return Math.max(1, Number(item?.schrott_menge) || 1);
  }
  return Math.max(1, Number(item?.bware_menge) || 1);
};

/**
 * Öffnet den Etikett-Dialog für eine Position.
 * @param {Record<string, unknown>} item
 */
const openEtikettDialog = (item) => {
  if (!item?.ean) {
    toast.warning('Kein EAN vorhanden – Etikett nicht möglich');
    return;
  }
  const hasBware = Number(item.bware_menge) > 0;
  const hasSchrott = Number(item.schrott_menge) > 0;
  const typ = hasBware ? 'bware' : hasSchrott ? 'schrott' : 'bware';
  etikettData.value = {
    item,
    typ,
    anzahl: suggestEtikettAnzahl(item, typ),
  };
  try {
    etikettPreviewBarcode.value = createBarcodeDataUrl(String(item.ean), false);
  } catch (e) {
    console.warn(e);
    etikettPreviewBarcode.value = '';
  }
  dialogs.value.etikett = true;
};

/**
 * Passt die Etiketten-Anzahl an, wenn B-Ware/Schrott gewechselt wird.
 * @param {'bware'|'schrott'} typ
 */
const onEtikettTypChange = (typ) => {
  const item = etikettData.value.item;
  if (!item) return;
  etikettData.value.anzahl = suggestEtikettAnzahl(item, typ);
};

/**
 * Erzeugt HTML fuer ein einzelnes Etikett (alter Brother-Stil: EAN unten, Text oben).
 * @param {string} typLabel
 * @param {string} bez
 * @param {string} cmmf
 * @param {string} cref
 * @param {string} barcodeDataUrl
 * @param {string} ean
 * @returns {string}
 */
const buildSingleEtikettHtml = (typLabel, bez, cmmf, cref, barcodeDataUrl, ean) => `
  <div class="label">
    <div class="barcode-wrap">
      <img class="barcode" src="${barcodeDataUrl}" alt="EAN" />
    </div>
    <div class="texts">
      <div class="typ">${escapeHtml(typLabel)}</div>
      <div class="line">${escapeHtml(bez)}</div>
      <div class="line">${escapeHtml(cmmf)}</div>
      <div class="line">${escapeHtml(cref)}</div>
    </div>
  </div>`;

/**
 * Oeffnet ein Druckfenster mit allen uebergebenen Etiketten (HTML-Methode).
 * @param {Array<{typLabel:string,bez:string,cmmf:string,cref:string,ean:string}>} labelItems
 */
const openEtikettPrintWindow = (labelItems) => {
  const labelsHtml = labelItems
    .map(({ typLabel, bez, cmmf, cref, ean }) => {
      const barcodeUrl = createBarcodeDataUrl(ean, true);
      return buildSingleEtikettHtml(typLabel, bez, cmmf, cref, barcodeUrl, ean);
    })
    .join('');

  const html = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<title>Etiketten</title>
<style>
  @page {
    size: 90mm 26mm;
    margin: 0;
  }
  html, body {
    margin: 0;
    padding: 0;
    width: 90mm;
    background: #fff;
  }
  .label {
    width: 90mm;
    height: 26mm;
    box-sizing: border-box;
    padding: 2mm 3mm;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 3mm;
    page-break-after: always;
  }
  .label:last-child {
    page-break-after: auto;
  }
  .texts {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.5mm;
  }
  .typ {
    font-family: Arial, sans-serif;
    font-size: 9pt;
    font-weight: 700;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .line {
    font-family: Arial, sans-serif;
    font-size: 7.5pt;
    font-weight: 400;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .barcode-wrap {
    flex: 0 0 auto;
    width: 42mm;
    height: 20mm;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .barcode {
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
</style>
</head>
<body>${labelsHtml}</body>
</html>`;

  const win = window.open('', '_blank', 'width=400,height=300');
  if (!win) {
    toast.error('Popup wurde blockiert – bitte Popups erlauben');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
    setTimeout(() => win.close(), 1000);
  }, 500);
};

/**
 * Druckt Etiketten (90x26 mm) fuer die aktuelle Position.
 */
const printEtiketten = () => {
  const item = etikettData.value.item;
  if (!item?.ean) {
    toast.warning('Kein EAN vorhanden');
    return;
  }

  const anzahl = Math.min(99, Math.max(1, Number(etikettData.value.anzahl) || 1));
  const typLabel = etikettTypLabel.value;
  const bez = item.bezeichnung || '';
  const cmmf = item.cmmf_nummer || '';
  const cref = item.commercial_ref || '';
  const ean = normalizeEan13(String(item.ean || ''));

  const labelItems = Array.from({ length: anzahl }, () => ({ typLabel, bez, cmmf, cref, ean }));
  openEtikettPrintWindow(labelItems);
  dialogs.value.etikett = false;
};

/**
 * Druckt alle B-Ware-Etiketten in einem Lauf (Bulk).
 */
const printBulkBwareEtiketten = () => {
  if (bulkBwareLabelCount.value === 0) {
    toast.warning('Keine B-Ware-Etiketten zum Drucken');
    return;
  }

  const labelItems = [];
  let skippedCount = 0;

  for (const item of positionen.value) {
    const anzahl = Math.max(0, Number(item?.bware_menge) || 0);
    if (anzahl < 1) continue;
    if (!item?.ean) {
      skippedCount += anzahl;
      continue;
    }
    const bez = item.bezeichnung || '';
    const cmmf = item.cmmf_nummer || '';
    const cref = item.commercial_ref || '';
    const ean = normalizeEan13(String(item.ean || ''));
    for (let i = 0; i < anzahl; i++) {
      labelItems.push({ typLabel: 'B-Ware', bez, cmmf, cref, ean });
    }
  }

  if (labelItems.length === 0) {
    toast.warning('Keine druckbaren B-Ware-Etiketten vorhanden');
    return;
  }

  openEtikettPrintWindow(labelItems);
  if (skippedCount > 0) {
    toast.warning(`${skippedCount} Etikett(en) ohne EAN wurden übersprungen`);
  }
};

/**
 * Baut den Inhalt eines Scheins (ohne HTML-Rahmen).
 * @param {boolean} isPalettenSchein
 * @param {Array} posData
 * @param {string|null} bwareId
 * @param {'bware'|'eingangskontrolle'} mode
 * @returns {string}
 */
const buildScheinBody = (
  isPalettenSchein = false,
  posData = null,
  bwareId = null,
  mode = 'bware'
) => {
  const isEk = mode === 'eingangskontrolle';
  const datum = new Date().toLocaleString('de-DE');
  let sumBware = 0;
  let sumSchrott = 0;
  let sumGesamt = 0;
  const rows = (posData || positionen.value)
    .map((p) => {
      const b = Number(p.bware_menge || 0);
      const s = Number(p.schrott_menge || 0);
      const gesamt = b + s;
      sumBware += b;
      sumSchrott += s;
      sumGesamt += gesamt;
      if (isPalettenSchein) {
        // Eingangskontrolle: Anzahl = B-Ware ohne Schrott; sonst Gesamtmenge
        const anzahl = isEk ? b : gesamt;
        return (
          `<tr>` +
            `<td>${escapeHtml(p.cmmf_nummer || '')}</td>` +
            `<td>${escapeHtml(p.commercial_ref || '')}</td>` +
            `<td style="text-align:center;">${anzahl}</td>` +
          `</tr>`
        );
      }
      // Eingangskontrolle: Gesamtzahl inkl. Schrott; sonst B-Ware-Spalte
      const firstQty = isEk ? gesamt : b;
      return (
        `<tr>` +
          `<td>${escapeHtml(p.cmmf_nummer || '')}</td>` +
          `<td>${escapeHtml(p.commercial_ref || '')}</td>` +
          `<td style="text-align:center;">${firstQty}</td>` +
          `<td style="text-align:center;">${s}</td>` +
        `</tr>`
      );
    })
    .join('');

  const palettenSum = isEk ? sumBware : sumGesamt;
  const detailFirstSum = isEk ? sumGesamt : sumBware;

  const totalsRow = isPalettenSchein
    ? (
        `<tr class="totals">` +
        `<td colspan="2"><strong>GESAMT</strong></td>` +
        `<td style="text-align:center;"><strong>${palettenSum}</strong></td>` +
        `</tr>`
      )
    : (
        `<tr class="totals">` +
        `<td colspan="2"><strong>GESAMT</strong></td>` +
        `<td style="text-align:center;"><strong>${detailFirstSum}</strong></td>` +
        `<td style="text-align:center;"><strong>${sumSchrott}</strong></td>` +
        `</tr>`
      );

  const headline = isPalettenSchein
    ? 'PALETTENSCHEIN'
    : isEk
      ? 'EINGANGSKONTROLLE'
      : 'B-WARE / SCHROTT SCHEIN';
  const qtyHeader = isPalettenSchein
    ? isEk
      ? 'Anzahl'
      : 'Gesamtmenge'
    : isEk
      ? 'Gesamtzahl'
      : 'B-Ware';
  const schrottHeader = isEk ? 'davon Schrott' : 'Schrott';
  const columns = isPalettenSchein
    ? `
        <th>CMMF</th>
        <th>Commercial Ref</th>
        <th>${qtyHeader}</th>
      `
    : `
        <th>CMMF</th>
        <th>Commercial Ref</th>
        <th>${qtyHeader}</th>
        <th>${schrottHeader}</th>
      `;
  const emptyRow = isPalettenSchein
    ? '<tr><td colspan="3" style="text-align:center;color:#888;">Keine Positionen</td></tr>'
    : '<tr><td colspan="4" style="text-align:center;color:#888;">Keine Positionen</td></tr>';

  return `
  <div class="schein-page">
    <div class="header">
      <h1>${headline}</h1>
      ${bwareId ? `<div class="bware-id">${escapeHtml(bwareId)}</div>` : ''}
      <div class="datum">${escapeHtml(datum)}</div>
    </div>
    <table class="pos">
      <thead>
        <tr>
          ${columns}
        </tr>
      </thead>
      <tbody>
        ${rows || emptyRow}
        ${totalsRow}
      </tbody>
    </table>
  </div>`;
};

const scheinPrintStyles = `
  @page { size: A4; margin: 12mm; }
  body { font-family: Arial, sans-serif; font-size: 11pt; color: #000; margin: 0; }
  .schein-page { page-break-after: always; }
  .schein-page:last-child { page-break-after: auto; }
  .header { border-bottom: 2px solid #000; padding-bottom: 4mm; margin-bottom: 6mm; text-align: center; }
  .header h1 { margin: 0; font-size: 18pt; }
  .header .bware-id { font-size: 14pt; font-weight: 700; margin-top: 2mm; }
  .header .datum { margin-top: 2mm; font-size: 10pt; color: #333; }
  table.pos { width: 100%; border-collapse: collapse; }
  table.pos th, table.pos td { border: 1px solid #000; padding: 2mm 2.5mm; font-size: 10pt; }
  table.pos th { background: #e8e8e8; }
  tr.totals td { background: #f0f0f0; }
`;

/**
 * @param {boolean} isPalettenSchein
 * @param {Array|null} posData
 * @param {string|null} bwareId
 * @param {'bware'|'eingangskontrolle'} mode
 * @returns {string}
 */
const buildScheinHtml = (
  isPalettenSchein = false,
  posData = null,
  bwareId = null,
  mode = 'bware'
) => {
  const title = isPalettenSchein
    ? 'Palettenschein'
    : mode === 'eingangskontrolle'
      ? 'Eingangskontrolle'
      : 'B-Ware Schein';
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>${scheinPrintStyles}</style>
</head>
<body>
  ${buildScheinBody(isPalettenSchein, posData, bwareId, mode)}
</body>
</html>`;
};

/**
 * Baut Schein + Palettenschein in einem Druckdokument (2 Seiten).
 * @param {Array} posData
 * @param {string|null} bwareId
 * @param {'bware'|'eingangskontrolle'} mode
 * @returns {string}
 */
const buildBeideScheineHtml = (posData, bwareId = null, mode = 'bware') => {
  const idLabel = bwareId ? ` ${bwareId}` : '';
  const docTitle =
    mode === 'eingangskontrolle' ? `Eingangskontrolle Scheine${idLabel}` : `B-Ware Scheine${idLabel}`;
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${docTitle}</title>
  <style>${scheinPrintStyles}</style>
</head>
<body>
  ${buildScheinBody(false, posData, bwareId, mode)}
  ${buildScheinBody(true, posData, bwareId, mode)}
</body>
</html>`;
};

/**
 * Oeffnet ein Druckfenster fuer einen Schein.
 * @param {string} html
 */
const openScheinPrintWindow = (html) => {
  const win = window.open('', '_blank', 'width=900,height=900');
  if (!win) {
    toast.error('Popup blockiert – Drucken nicht möglich');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    try {
      win.print();
    } catch (e) {
      console.warn(e);
    }
  }, 350);
};

const archivBwareLabelCount = computed(() =>
  archivDetail.value.positionen.reduce(
    (sum, p) => sum + Math.max(0, Number(p?.bware_menge) || 0), 0
  )
);

/**
 * Druckt den Schein einer archivierten Liste (Popup).
 * @param {boolean} isPalettenSchein
 */
const printArchivScheinDocument = (isPalettenSchein = false) => {
  const pos = archivDetail.value.positionen;
  if (pos.length === 0) {
    toast.warning('Keine Positionen zum Drucken');
    return;
  }
  const bwareId = archivDetail.value.liste?.bware_id || null;
  openScheinPrintWindow(buildScheinHtml(isPalettenSchein, pos, bwareId));
};

const printArchivSchein = () => printArchivScheinDocument(false);
const printArchivPalettenSchein = () => printArchivScheinDocument(true);

/**
 * Bulk-Etikettendruck aus einer archivierten Liste.
 */
const printArchivBulkEtiketten = () => {
  const pos = archivDetail.value.positionen;
  const labelItems = [];
  let skippedCount = 0;
  for (const item of pos) {
    const anzahl = Math.max(0, Number(item?.bware_menge) || 0);
    if (anzahl < 1) continue;
    if (!item?.ean) {
      skippedCount += anzahl;
      continue;
    }
    const bez = item.bezeichnung || '';
    const cmmf = item.cmmf_nummer || '';
    const cref = item.commercial_ref || '';
    const ean = normalizeEan13(String(item.ean || ''));
    for (let i = 0; i < anzahl; i++) {
      labelItems.push({ typLabel: 'B-Ware', bez, cmmf, cref, ean });
    }
  }
  if (labelItems.length === 0) {
    toast.warning('Keine druckbaren B-Ware-Etiketten vorhanden');
    return;
  }
  openEtikettPrintWindow(labelItems);
  if (skippedCount > 0) {
    toast.warning(`${skippedCount} Etikett(en) ohne EAN wurden übersprungen`);
  }
};

onMounted(() => {
  fetchPositionen();
  fetchArchiv();
  if (canImport.value) {
    fetchMaterialStats();
  }
  focusScanner();
});
</script>

<style scoped>
.bware-container {
  padding: 20px;
}

.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.info-box {
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 8px;
}

.info-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.dialog-card {
  background-color: rgba(30, 30, 35, 0.95);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

code {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.etikett-dialog-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.etikett-preview-col {
  width: 100%;
}

.etikett-controls-col {
  width: 100%;
}

.etikett-preview-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.etikett-preview-stage {
  display: flex;
  justify-content: center;
  padding: 8px 4px 16px;
  overflow: hidden;
}

/* Traegerfolie */
.etikett-backing {
  --etikett-scale: 2.6;
  width: calc(96mm * var(--etikett-scale));
  height: calc(32mm * var(--etikett-scale));
  max-width: 100%;
  background: linear-gradient(90deg, #f4ebd8 0%, #ebe1c9 50%, #e5d8bc 100%);
  border-radius: 2px;
  box-shadow:
    inset 0 0 0 1px rgba(0, 0, 0, 0.07),
    0 10px 28px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Weisses Etikett */
.etikett-preview {
  width: calc(90mm * var(--etikett-scale));
  height: calc(26mm * var(--etikett-scale));
  background: #ffffff;
  border-radius: calc(1.5mm * var(--etikett-scale));
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.14),
    0 0 0 0.5px rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.etikett-preview-inner {
  width: 100%;
  height: 100%;
  padding: calc(2mm * var(--etikett-scale)) calc(3mm * var(--etikett-scale));
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: calc(3mm * var(--etikett-scale));
  color: #000;
  font-family: Arial, Helvetica, sans-serif;
  box-sizing: border-box;
}

.etikett-preview-barcode-wrap {
  flex: 0 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.etikett-preview-barcode {
  height: 100%;
  width: auto;
  max-width: 100%;
  display: block;
  object-fit: contain;
  object-position: left center;
}

.etikett-preview-texts {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  line-height: 1.15;
}

.etikett-preview-typ {
  font-weight: 700;
  font-size: calc(10pt * var(--etikett-scale));
  line-height: 1.15;
  color: #000;
}

.etikett-preview-line {
  font-weight: 400;
  font-size: calc(8.5pt * var(--etikett-scale));
  line-height: 1.15;
  color: #000;
  word-break: break-word;
}

@media (max-width: 700px) {
  .etikett-backing {
    --etikett-scale: 1.35;
  }
}
</style>
